/**
 * V70 엔글 타자왕 — 리더보드 소속 열 라벨 산출 유틸
 *
 * 사용자 지시 §2 반영:
 *   - LeaderboardRow 의 orgUnitId 를 받아 OrgUnit map 에서 현재 조직명을 매핑
 *   - 조직 미지정 / orgUnit 삭제 / 조회 실패 → 임의 조직명 생성 금지, 대체 라벨 사용
 *   - 샘플 (uid prefix `demo-`) 은 실제 직원과 구분해 "샘플" 로 명시
 *   - companyCode 는 소속 표시용으로 사용하지 않음 (내부 처리 유지)
 *   - 조직 경로 전체가 아니라 **배정된 조직 그 자체의 name 만**
 */
import type { OrgUnit } from '../../../types/orgUnit';
import type { LeaderboardRow } from './types';

export const SAMPLE_UID_PREFIX = 'demo-';

export interface OrgLabel {
  text: string;
  /** 라벨 종류 — UI 색상 분기 등에 사용 */
  kind: 'sample' | 'org' | 'unassigned' | 'archived' | 'unknown';
}

/**
 * row 와 OrgUnit map 으로부터 소속 라벨 산출.
 *
 * - row.uid 가 샘플 → `{ sample, "샘플" }`
 * - row.orgUnitId 없음 → `{ unassigned, "미지정" }`
 * - orgUnit 조회 실패 → `{ unknown, "조회 불가" }`
 * - orgUnit.status === 'archived' → `{ archived, "<원래 이름> (해체)" }` — name 은 저장된 이름 그대로
 * - 정상 → `{ org, orgUnit.name }`
 */
export function resolveLeaderboardOrgName(
  row: Pick<LeaderboardRow, 'uid' | 'orgUnitId'>,
  orgMap: Map<string, OrgUnit>
): OrgLabel {
  if (row.uid.startsWith(SAMPLE_UID_PREFIX)) {
    return { text: '샘플', kind: 'sample' };
  }
  if (!row.orgUnitId) {
    return { text: '미지정', kind: 'unassigned' };
  }
  const org = orgMap.get(row.orgUnitId);
  if (!org) {
    return { text: '조회 불가', kind: 'unknown' };
  }
  if (org.status === 'archived') {
    return { text: `${org.name} (해체)`, kind: 'archived' };
  }
  return { text: org.name, kind: 'org' };
}

/**
 * orgUnits 리스트에서 Map 구성 (리더보드 테이블 한 번만 사용).
 */
export function buildOrgMap(orgUnits: OrgUnit[]): Map<string, OrgUnit> {
  const m = new Map<string, OrgUnit>();
  for (const o of orgUnits) m.set(o.id, o);
  return m;
}
